import { Fragment, useEffect, useMemo, useState } from "react";
import type {
  Comment,
  CommentStatus,
  CommentType,
  VisibilityChange,
  WorkspaceMember,
} from "../../api/types";
import { MessagesSquare } from "../../compose/Icon";
import { EmptyState } from "../../compose/states";
import type { AnchorState } from "../../lib/anchoring";
import type { DocumentOrder } from "../../lib/reviewOrder";
import {
  loadReviewSort,
  type ReviewSort,
  saveReviewSort,
  sortRoots,
  unanchoredRoots,
} from "../../lib/reviewSort";
import { buildThreads } from "../../lib/threads";
import { CommentThread, type ThreadCallbacks } from "./CommentThread";
import { ReviewNotesSort } from "./ReviewNotesSort";
import { NO_UNANCHORED, UnanchoredHeading } from "./UnanchoredHeading";

/**
 * The review rail (design screens §3 · "living marginalia"): the anchored
 * threads for a document, with two pill filters (type + status). Each anchored
 * root shows its type/status/author/rich body/mentions + nested replies + a
 * reply composer + status controls; clicking a thread scrolls to and blooms its
 * anchored text on the canvas.
 */

type TypeFilter = "all" | CommentType;
type StatusFilter = "all" | CommentStatus;

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: "all", label: "All types" },
  { value: "comment", label: "Comment" },
  { value: "suggestion", label: "Suggestion" },
  { value: "request-for-change", label: "Change" },
];

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "acted_on", label: "Acted-on" },
  { value: "pinned", label: "Pinned" },
];

export function ReviewRail({
  comments,
  members,
  anchorStates,
  documentOrder,
  onReply,
  onStatus,
  onFocusAnchor,
  slug,
  docId,
  onCopyLink,
  linkedCommentId,
  onHoverCard,
  viewerId,
  viewerIsStaff,
  onVisibility,
  onEdit,
  onToggleReaction,
  revealCommentId,
  onDelete,
}: {
  comments: Comment[];
  members: WorkspaceMember[];
  anchorStates: Record<number, AnchorState>;
  /** WHERE each note sits in the document — the read surface's live anchor
   *  resolution. Optional: without it "Document order" still orders by each
   *  note's RECORDED anchor (a real document position, one paint stale), never
   *  by creation time. See `lib/reviewOrder`. */
  documentOrder?: DocumentOrder | null;
  onReply: (
    parentId: number,
    draft: { body: string; body_format: string; mentions: number[] },
  ) => Promise<void>;
  onStatus: (commentId: number, status: CommentStatus) => Promise<void>;
  onFocusAnchor: (commentId: number) => void;
  slug: string;
  docId: string;
  onCopyLink?: (url: string) => void;
  linkedCommentId?: number | null;
  onHoverCard?: (commentId: number | null) => void;
  /** comment-visibility (§6A): viewer identity + change-scope handler. */
  viewerId?: number;
  viewerIsStaff?: boolean;
  onVisibility?: (commentId: number, body: VisibilityChange) => Promise<void>;
  /** review-notes §3: persist an author body edit (author-only). */
  onEdit?: (
    commentId: number,
    draft: { body: string; body_format: string },
  ) => Promise<void>;
  /** review-notes §4: toggle the viewer's emoji reaction on a comment/reply. */
  onToggleReaction?: (commentId: number, emoji: string, add: boolean) => void;
  /** review-notes §2.3: deep-linked comment/reply id → auto-expand its thread. */
  revealCommentId?: number | null;
  /** comment-delete: delete a comment/reply (AUTHOR-ONLY, enforced server-side).
   *  Absent ⇒ no delete affordance. The host owns the reload — a deleted note
   *  with no replies DISAPPEARS, one with replies returns as a placeholder, and
   *  guessing which happened client-side would be wrong half the time. */
  onDelete?: (commentId: number) => Promise<void>;
}) {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  // review-notes §1.4: the sort is a GLOBAL per-reviewer preference (localStorage).
  const [sort, setSort] = useState<ReviewSort>(() => loadReviewSort());
  useEffect(() => {
    saveReviewSort(sort);
  }, [sort]);

  const roots = useMemo(
    () => sortRoots(buildThreads(comments), sort, documentOrder),
    [comments, sort, documentOrder],
  );
  const shown = roots.filter(
    (t) =>
      (typeFilter === "all" || t.comment.type === typeFilter) &&
      (statusFilter === "all" || t.comment.status === statusFilter),
  );
  // Document order is the ONLY sort with a residue: a note whose quote the
  // surface can no longer find has no position at all. `sortRoots` parks those
  // at the end; the rail NAMES them rather than letting them look like the
  // bottom of the document.
  const unplaced =
    sort === "citation" ? unanchoredRoots(shown, documentOrder) : NO_UNANCHORED;
  const firstUnplaced = shown.findIndex((t) => unplaced.has(t.comment.id));

  const cb: ThreadCallbacks = {
    members,
    onReply,
    onStatus,
    onFocusAnchor,
    anchorStates,
    slug,
    docId,
    onCopyLink,
    linkedCommentId,
    onHoverCard,
    viewerId,
    viewerIsStaff,
    onVisibility,
    onEdit,
    onToggleReaction,
    revealCommentId,
    onDelete,
  };

  return (
    <div className="rail__card" data-testid="review-rail">
      <div
        className="rail__header"
        data-testid="review-notes-header"
        data-sort={sort}
      >
        <h3 className="rail__h">
          Review notes
          {roots.length > 0 ? <span className="rail__count">{roots.length}</span> : null}
        </h3>
        {roots.length > 0 ? (
          <ReviewNotesSort sort={sort} onSort={setSort} />
        ) : null}
      </div>

      {roots.length > 0 ? (
        <div className="rail__filters">
          <div className="rail__filter-row" role="radiogroup" aria-label="Filter by type">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="radio"
                aria-checked={typeFilter === f.value}
                className={`filter-pill${typeFilter === f.value ? " filter-pill--active" : ""}`}
                data-testid={`filter-type-${f.value}`}
                onClick={() => setTypeFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="rail__filter-row" role="radiogroup" aria-label="Filter by status">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="radio"
                aria-checked={statusFilter === f.value}
                className={`filter-pill${statusFilter === f.value ? " filter-pill--active" : ""}`}
                data-testid={`filter-status-${f.value}`}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {roots.length === 0 ? (
        <EmptyState
          icon={<MessagesSquare size={26} strokeWidth={1.5} />}
          title="No comments yet"
          body="Select any text in the document to pin a comment to it."
        />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={<MessagesSquare size={26} strokeWidth={1.5} />}
          title="No matching comments"
          body="No thread matches the current type / status filter."
        />
      ) : (
        <div className="thread-list" data-testid="thread-list">
          {shown.map((t, i) => (
            <Fragment key={t.comment.id}>
              {i === firstUnplaced ? (
                <UnanchoredHeading count={shown.length - firstUnplaced} />
              ) : null}
              <CommentThread node={t} depth={0} cb={cb} />
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
